/**
 * imageUtils.js  — 문서/계약서 공통 UI 유틸
 *
 * DocFormManager.js, ContractManager.js 양쪽에서 중복 구현된 컴포넌트/로직을 통합:
 *   - CropTool         : 이미지 크롭 UI (두 파일에 95% 동일하게 중복)
 *   - ImageViewer      : 이미지 뷰어 모달 (로딩 스피너 + base64 이미지 표시)
 *   - ImageUploadBox   : 파일 선택 + 미리보기 + 크롭 조정 버튼 (계약서 앞/뒷면 패턴)
 *   - useImageCache    : base64 이미지 캐시 훅 (fetch → setImgCache 패턴 통합)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE = 'https://adria.terrier-ule.ts.net';

// ══════════════════════════════════════════════════════════════
// CropTool — 드래그 가능한 크롭 박스
// (DocFormManager.js 와 ContractManager.js 에서 동일하게 중복 구현)
// ══════════════════════════════════════════════════════════════
export function CropTool({ src, onDone, onCancel }) {
  const containerRef = useRef(null);
  const [box, setBox]   = useState({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
  const boxRef          = useRef({ x: 0.1, y: 0.1, w: 0.8, h: 0.8 });
  const dragging        = useRef(null);
  const startRef        = useRef(null);

  const updateBox = (val) => { boxRef.current = val; setBox(val); };
  const clamp     = (v, mn = 0, mx = 1) => Math.min(mx, Math.max(mn, v));
  const getXY     = (e) =>
    e.touches
      ? { cx: e.touches[0].clientX, cy: e.touches[0].clientY }
      : { cx: e.clientX,            cy: e.clientY };

  const startDrag = (e, handle) => {
    e.preventDefault();
    e.stopPropagation();
    dragging.current = handle;
    const { cx, cy } = getXY(e);
    startRef.current  = { mx: cx, my: cy, ...boxRef.current };
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current || !containerRef.current) return;
      const { width, height } = containerRef.current.getBoundingClientRect();
      const { cx, cy } = getXY(e);
      const dx = (cx - startRef.current.mx) / width;
      const dy = (cy - startRef.current.my) / height;
      const s  = startRef.current;
      if (dragging.current === 'move') {
        updateBox({ x: clamp(s.x + dx, 0, 1 - s.w), y: clamp(s.y + dy, 0, 1 - s.h), w: s.w, h: s.h });
      } else {
        let { x, y, w, h } = s;
        if (dragging.current.includes('e'))  w = clamp(s.w + dx, 0.05, 1 - s.x);
        if (dragging.current.includes('s'))  h = clamp(s.h + dy, 0.05, 1 - s.y);
        if (dragging.current.includes('w')) { const nx = clamp(s.x + dx, 0, s.x + s.w - 0.05); x = nx; w = s.x + s.w - nx; }
        if (dragging.current.includes('n')) { const ny = clamp(s.y + dy, 0, s.y + s.h - 0.05); y = ny; h = s.y + s.h - ny; }
        updateBox({ x, y, w, h });
      }
    };
    const onUp = () => { dragging.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend',  onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend',  onUp);
    };
  }, []);

  const HANDLES = [
    ['nw', '0%',  '0%' ], ['ne', '100%', '0%'  ],
    ['sw', '0%',  '100%'], ['se', '100%', '100%'],
    ['n',  '50%', '0%' ], ['s',  '50%',  '100%'],
    ['w',  '0%',  '50%'], ['e',  '100%', '50%' ],
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.9)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#fff', marginBottom: '12px', fontSize: '14px', fontWeight: 'bold' }}>
        크롭 영역을 조절하세요
      </div>
      <div ref={containerRef}
        style={{ position: 'relative', maxWidth: '90vw', maxHeight: '70vh', lineHeight: 0, userSelect: 'none' }}>
        <img src={src} style={{ maxWidth: '90vw', maxHeight: '70vh', display: 'block' }} alt="crop" />
        <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', pointerEvents: 'none' }} />
        <div
          style={{ position: 'absolute', left: `${box.x * 100}%`, top: `${box.y * 100}%`,
                   width: `${box.w * 100}%`, height: `${box.h * 100}%`,
                   border: '2px solid #06b6d4', boxSizing: 'border-box', cursor: 'move' }}
          onMouseDown={(e) => startDrag(e, 'move')}
          onTouchStart={(e) => startDrag(e, 'move')}
        >
          {HANDLES.map(([h, l, t]) => (
            <div key={h}
              style={{ position: 'absolute', left: l, top: t, transform: 'translate(-50%,-50%)',
                       width: '18px', height: '18px', backgroundColor: '#06b6d4',
                       borderRadius: '4px', cursor: `${h}-resize`, zIndex: 2, touchAction: 'none' }}
              onMouseDown={(e) => startDrag(e, h)}
              onTouchStart={(e) => startDrag(e, h)}
            />
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
        <button onClick={onCancel}
          style={{ padding: '9px 20px', backgroundColor: '#4b5563', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
          취소
        </button>
        <button onClick={() => onDone(box)}
          style={{ padding: '9px 20px', backgroundColor: '#06b6d4', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
          ✅ 이대로 사용
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ImageViewer — 이미지 뷰어 모달 (공통 패턴)
// DocFormManager와 ContractManager에서 로딩 스피너 + 이미지 표시가 동일하게 중복
// ══════════════════════════════════════════════════════════════

/**
 * @param {object} props
 * @param {string}   props.title        - 헤더 제목 텍스트
 * @param {string}   [props.subtitle]   - 헤더 부제목 (옵션)
 * @param {string}   [props.imgData]    - base64 이미지 데이터
 * @param {boolean}  [props.loading]    - 로딩 중 여부
 * @param {ReactNode}[props.tabBar]     - 탭 바 (계약서 앞/뒤 등, 옵션)
 * @param {function} props.onClose      - 닫기 핸들러
 */
export function ImageViewer({ title, subtitle, imgData, loading, tabBar, pdfBtn, onClose }) {
  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 1000,
                 display: 'flex', flexDirection: 'column' }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '14px 16px', backgroundColor: '#1f2937' }}>
          <div>
            <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '14px' }}>{title}</span>
            {subtitle && (
              <span style={{ color: '#9ca3af', fontSize: '11px', marginLeft: '8px' }}>{subtitle}</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {pdfBtn}
            <button onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#fff', fontSize: '20px', cursor: 'pointer' }}>✕</button>
          </div>
        </div>

        {/* 탭바 (선택) */}
        {tabBar}

        {/* 이미지 영역 */}
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', padding: '10px' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '40px', border: '4px solid #4f46e5',
                            borderTopColor: 'transparent', borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite' }} />
              <span style={{ color: '#9ca3af', fontSize: '13px' }}>이미지 로딩 중...</span>
            </div>
          ) : imgData ? (
            <img
              src={imgData}
              alt="viewer"
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '8px',
                       userSelect: 'none', WebkitUserSelect: 'none', pointerEvents: 'none' }}
            />
          ) : (
            <span style={{ color: '#9ca3af', fontSize: '13px' }}>이미지를 불러올 수 없습니다</span>
          )}
        </div>

      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════
// ImageUploadBox — 파일 선택 + 미리보기 + 크롭 조정
// ContractManager의 앞면/뒷면 업로드 박스 패턴 컴포넌트화
// ══════════════════════════════════════════════════════════════

/**
 * @param {object} props
 * @param {string}   props.label        - 라벨 (예: '계약서 이미지')
 * @param {string}   [props.labelNote]  - 라벨 옆 보조 텍스트 (예: '(변경 시만 선택)')
 * @param {string}   [props.icon]       - 아이콘 이모지 (기본 '📄')
 * @param {string}   props.preview      - 미리보기 base64 or null
 * @param {boolean}  props.hasCrop      - 크롭 완료 여부
 * @param {function} props.onFileChange - 파일 선택 시 (file, previewDataUrl) => void
 * @param {function} props.onCropClick  - 크롭 조정 버튼 클릭
 * @param {object}   [props.style]      - 추가 스타일
 */
export function ImageUploadBox({ label, labelNote, icon = '📄', preview, hasCrop, onFileChange, onCropClick, style = {} }) {
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onFileChange(file, ev.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div style={{ marginBottom: '10px', ...style }}>
      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px', fontWeight: '600' }}>
        {label}
        {labelNote && <span style={{ color: '#9ca3af', fontWeight: 'normal', marginLeft: '4px' }}>{labelNote}</span>}
      </div>
      <label style={{ display: 'block', border: '2px dashed #c7d2fe', borderRadius: '10px',
                      padding: '16px', textAlign: 'center', cursor: 'pointer',
                      backgroundColor: '#f5f3ff', marginTop: '4px' }}>
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleChange} />
        {preview ? (
          <div>
            <img src={preview} alt="preview"
              style={{ maxHeight: '120px', maxWidth: '100%', borderRadius: '7px', objectFit: 'contain' }} />
            {hasCrop && <div style={{ color: '#4f46e5', fontSize: '11px', marginTop: '4px' }}>✅ 크롭 완료</div>}
            <div style={{ color: '#7c3aed', fontSize: '11px', marginTop: '4px' }}>클릭하면 다시 선택</div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '28px', marginBottom: '4px' }}>{icon}</div>
            <div style={{ color: '#7c3aed', fontSize: '13px', fontWeight: '600' }}>{label} 선택</div>
            <div style={{ color: '#a78bfa', fontSize: '11px', marginTop: '3px' }}>JPG, PNG 등</div>
          </div>
        )}
      </label>
      {preview && (
        <button onClick={onCropClick}
          style={{ width: '100%', padding: '7px', marginTop: '6px', backgroundColor: '#eff6ff',
                   color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '8px',
                   cursor: 'pointer', fontSize: '12px' }}>
          ✂️ 크롭 조정
        </button>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// useImageCache — base64 이미지 캐시 훅
// DocFormManager, ContractManager 양쪽에서 동일한 패턴 사용
// ══════════════════════════════════════════════════════════════

/**
 * @param {function} fetcher - (key) => Promise<string | null>  (base64 반환)
 * @returns {{ cache, loading, fetch: fetchFn }}
 */
export function useImageCache(fetcher) {
  const [cache,   setCache]   = useState({});
  const [loading, setLoading] = useState({});

  const fetchImage = useCallback(async (key) => {
    if (!key || cache[key] || loading[key]) return;
    setLoading((l) => ({ ...l, [key]: true }));
    try {
      const data = await fetcher(key);
      if (data) setCache((c) => ({ ...c, [key]: data }));
    } catch {}
    finally { setLoading((l) => ({ ...l, [key]: false })); }
  }, [cache, loading, fetcher]);

  const clearCache = useCallback((keys) => {
    if (keys) {
      setCache((c) => { const n = {...c}; keys.forEach(k => delete n[k]); return n; });
    } else {
      setCache({});
    }
  }, []);

  return { cache, loading, fetchImage, clearCache };
}

// ══════════════════════════════════════════════════════════════
// useFileRead — FileReader를 Promise로 래핑
// ══════════════════════════════════════════════════════════════
export function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
